import { Search, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '../../primitives/Button'
import { Input } from '../../primitives/Input'

interface SearchFilterProps {
  /**
   * Current search value from parent
   */
  value: string
  /**
   * Callback when search value changes (debounced)
   */
  onChange: (_value: string) => void
  /**
   * Placeholder text
   */
  placeholder?: string
  /**
   * Only allow numbers
   */
  onlyNumbers?: boolean
  /**
   * Debounce delay in milliseconds
   */
  debounceDelay?: number
  /**
   * Custom className
   */
  className?: string
}

/**
 * SearchFilter - Reusable search input with debouncing
 * Based on containers SearchQuery component pattern
 */
export function SearchFilter({
  value,
  onChange,
  placeholder = 'Buscar...',
  onlyNumbers = false,
  debounceDelay = 500,
  className,
}: SearchFilterProps) {
  const [searchValue, setSearchValue] = useState(value)
  const timeoutRef = useRef<NodeJS.Timeout>()
  const isImmediateAction = useRef(false)
  const lastPropValue = useRef(value)
  const lastDebouncedValue = useRef('')
  const externalClearDetected = useRef(false)

  // Sync with external prop changes (e.g., clear all filters)
  useEffect(() => {
    if (value !== lastPropValue.current) {
      lastPropValue.current = value

      // Detect external clear (when value becomes empty from non-empty)
      if (value === '' && searchValue !== '' && !isImmediateAction.current) {
        externalClearDetected.current = true
        setSearchValue('')
        return
      }

      setSearchValue(value)
    }
  }, [value, searchValue])

  // Debounced search effect
  useEffect(() => {
    if (isImmediateAction.current) {
      isImmediateAction.current = false
      return
    }

    if (externalClearDetected.current) {
      externalClearDetected.current = false
      return
    }

    // Don't call onChange if the debounced value hasn't actually changed
    if (searchValue === lastDebouncedValue.current) {
      return
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      lastDebouncedValue.current = searchValue
      onChange(searchValue)
    }, debounceDelay)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [searchValue, onChange, debounceDelay])

  // Input change handler
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = onlyNumbers
        ? e.target.value.replace(/[^0-9]/g, '')
        : e.target.value

      setSearchValue(newValue)

      // Reset external action flags when user starts typing
      isImmediateAction.current = false
      externalClearDetected.current = false
    },
    [onlyNumbers]
  )

  // Clear function with immediate action
  const handleClear = useCallback(() => {
    setSearchValue('')
    isImmediateAction.current = true

    // Reset tracking refs
    lastDebouncedValue.current = ''
    lastPropValue.current = ''

    onChange('')
  }, [onChange])

  return (
    <div
      className={`relative min-w-[200px] flex-1 md:min-w-[240px] ${className || ''}`}
    >
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
      <Input
        className="bg-white pl-9 pr-4"
        inputSize="md"
        onChange={handleInputChange}
        placeholder={placeholder}
        value={searchValue}
      />
      {searchValue && (
        <Button
          className="absolute right-1 top-1.5 h-7 w-7"
          onClick={handleClear}
          size="icon"
          variant="ghost"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
